import { NextPageContext } from 'next'

import { ApolloClient, ApolloLink } from '@apollo/client'
import { NextSSRInMemoryCache } from '@apollo/experimental-nextjs-app-support/ssr'
import { SentryLink } from 'apollo-link-sentry'
import axios from 'axios'
import nookies from 'nookies'

import { DefaultCookieOptionType, LocalKeyName, RequiredSsrCookieOptions } from 'src/constants'
import { httpLink, onErrorLink } from 'src/libs/apollo-wrapper'
import { dateDiffSecond } from 'src/libs/date'
import { RefreshAuthTokenDocument } from 'src/types/generated'

export type SaveAccessTokenInput = {
  accessToken: string
  accessTokenExpiresAt: Date
  ctx?: Pick<NextPageContext, 'res'>
}
export type SaveRefreshTokenInput = {
  refreshToken: string
  refreshTokenExpiresAt: Date
  ctx?: Pick<NextPageContext, 'res'>
}
export type SaveUsernameInput = {
  username: string
  usernameExpiresAt: Date
  ctx?: Pick<NextPageContext, 'res'>
}

type RefreshAccessTokenInput = {
  refreshToken: string
  username: string
}
type RefreshAccessTokenOutput = {
  accessToken: string
  accessTokenExpiresAt: Date
  refreshToken: string
  refreshTokenExpiresAt: Date
}

type LoadAccessTokenInput = {
  ctx?: Pick<NextPageContext, 'req'>
}
type LoadRefreshTokenInput = {
  ctx?: Pick<NextPageContext, 'req'>
}
type LoadUsernameInput = {
  ctx?: Pick<NextPageContext, 'req'>
}

type RemoveAccessTokenInput = {
  ctx?: Pick<NextPageContext, 'res'>
}
type RemoveRefreshTokenInput = {
  ctx?: Pick<NextPageContext, 'res'>
}
type RemoveUsernameInput = {
  ctx?: Pick<NextPageContext, 'res'>
}

export const AuthRepository = {
  saveAccessToken: (input: SaveAccessTokenInput) => {
    const { accessToken, accessTokenExpiresAt, ctx } = input

    const maxAge = dateDiffSecond(new Date(), accessTokenExpiresAt)
    const options: DefaultCookieOptionType = { ...RequiredSsrCookieOptions, maxAge }

    nookies.set(ctx ?? null, LocalKeyName.AccessToken, accessToken, options)
  },

  saveRefreshToken: async (input: SaveRefreshTokenInput) => {
    const { refreshToken, refreshTokenExpiresAt, ctx } = input

    if (ctx) {
      const maxAge = dateDiffSecond(new Date(), refreshTokenExpiresAt)
      const options: DefaultCookieOptionType = {
        ...RequiredSsrCookieOptions,
        maxAge,
        httpOnly: true,
      }

      nookies.set(ctx, LocalKeyName.RefreshToken, refreshToken, options)
    } else {
      await axios.post('/api/save-refresh-token', {
        refreshToken,
        refreshTokenExpiresAt,
      })
    }
  },

  saveUsername: async (input: SaveUsernameInput) => {
    const { username, usernameExpiresAt, ctx } = input

    if (ctx) {
      const maxAge = dateDiffSecond(new Date(), usernameExpiresAt)
      const options: DefaultCookieOptionType = {
        ...RequiredSsrCookieOptions,
        maxAge,
        httpOnly: true,
      }

      nookies.set(ctx, LocalKeyName.Username, username, options)
    } else {
      await axios.post('/api/save-username', {
        username,
        usernameExpiresAt,
      })
    }
  },

  loadAccessToken: (input?: LoadAccessTokenInput): string | null => {
    const cookies = nookies.get(input?.ctx ?? null)
    return cookies[LocalKeyName.AccessToken] ?? null
  },

  loadRefreshToken: async (input?: LoadRefreshTokenInput): Promise<string | null> => {
    if (!input) {
      const response = await axios.get('/api/get-refresh-token')
      return response.data?.refreshToken ?? null
    }

    const cookies = nookies.get(input?.ctx ?? null)
    return cookies[LocalKeyName.RefreshToken] ?? null
  },

  loadUsername: async (input?: LoadUsernameInput): Promise<string | null> => {
    if (!input) {
      const response = await axios.get('/api/get-username')
      return response.data?.username ?? null
    }

    const cookies = nookies.get(input?.ctx ?? null)
    return cookies[LocalKeyName.Username] ?? null
  },

  removeAccessToken: async (input?: RemoveAccessTokenInput): Promise<void> => {
    if (!input) {
      await axios.delete('/api/remove-access-token')
      return
    }
    nookies.destroy(input?.ctx ?? null, LocalKeyName.AccessToken)
  },

  removeRefreshToken: async (input?: RemoveRefreshTokenInput): Promise<void> => {
    if (!input) {
      await axios.delete('/api/remove-refresh-token')
      return
    }
    nookies.destroy(input.ctx ?? null, LocalKeyName.RefreshToken)
  },

  removeUsername: async (input?: RemoveUsernameInput): Promise<void> => {
    if (!input) {
      await axios.delete('/api/remove-username')
      return
    }
    nookies.destroy(input.ctx ?? null, LocalKeyName.Username)
  },

  /** リフレッシュトークンを元に認証情報を再取得する */
  refreshAndSaveTokens: async ({
    refreshToken,
    username,
  }: RefreshAccessTokenInput): Promise<RefreshAccessTokenOutput | null> => {
    // TODO: トークン更新処理の見直し https://github.com/STYLE-PORT/roov-works-client/issues/471
    const client = new ApolloClient({
      link: ApolloLink.from([new SentryLink(), onErrorLink, httpLink]),
      cache: new NextSSRInMemoryCache(),
    })

    const response = await client.mutate({
      variables: { input: { refreshToken, cognitoUsername: username } },
      mutation: RefreshAuthTokenDocument,
    })
    const session = response.data?.refreshAccessToken.session
    if (!session) {
      return null
    }

    // 認証情報の再取得に成功した場合は、新たな認証情報を保存し、headerに設定する
    AuthRepository.saveAccessToken({
      accessToken: session.accessToken,
      accessTokenExpiresAt: session.accessTokenExpiresAt,
    })
    await AuthRepository.saveRefreshToken({
      refreshToken: session.refreshToken,
      refreshTokenExpiresAt: session.refreshTokenExpiresAt,
    })
    await AuthRepository.saveUsername({
      username,
      usernameExpiresAt: session.refreshTokenExpiresAt,
    })

    return {
      accessToken: session.accessToken,
      accessTokenExpiresAt: new Date(session.accessTokenExpiresAt),
      refreshToken: session.refreshToken,
      refreshTokenExpiresAt: new Date(session.refreshTokenExpiresAt),
    }
  },
}
